import type { TFunction } from 'i18next';

/**
 * Formats a large number into a human-readable string with suffixes (K, M, B).
 * Supports internationalization for locale-specific formatting and suffix translation.
 *
 * @param value - The number to format.
 * @param locale - The locale string (e.g., 'en-US').
 * @param t - Translation function from useTranslation.
 * @returns Formatted string or fallback for invalid inputs.
 * Usage:
 * import { useTranslation } from 'react-i18next';
 * import { formatLargeNumber } from '@shared/hooks/helpers/FormatLargeNumbers';
 *
 * const { t } = useTranslation('common');
 * <p>{formatLargeNumber(value, useCurrentUserStore.getState().userLocalePreferences.defaultLocale , t)}</p>
 */
export const formatLargeNumber = (value: number, locale: string, t: TFunction<'common'>): string => {
    if (typeof value !== 'number' || isNaN(value)) {
        return t('invalidNumber');
    }

    const formatter = new Intl.NumberFormat(locale, { notation: 'compact', compactDisplay: 'short' });
    return formatter.format(value);
};
